import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import {
  selectIpxEstimatedRegionBytes,
  selectIpxOrderMode,
  selectIpxRegionFloorArea,
  selectIpxRegionVolume,
  selectIpxSentScansLabel,
} from "@/store/modes/ipx-order-mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { Alert, FaroText, grey } from "@faro-lotv/flat-ui";
import { humanReadableBytes } from "@faro-lotv/foundation";
import { Stack } from "@mui/material";
import type { JSX } from "react";
import { SummaryList, SummaryRow } from "./order-placed-step";
import {
  ESTIMATED_POINT_COUNT_LABEL,
  formatFloorArea,
  formatVolume,
} from "./region-step";

/** @returns the summary of what is sent to IPX, whose terms are accepted in the IPX checkout */
export function ReviewPanel(): JSX.Element {
  const distanceUnit = useDistanceUnit();
  const { checkout } = useAppSelector(selectIpxOrderMode);
  const volume = useAppSelector(selectIpxRegionVolume);
  const floorArea = useAppSelector(selectIpxRegionFloorArea);
  const bytes = useAppSelector(selectIpxEstimatedRegionBytes);
  const scansLabel = useAppSelector(selectIpxSentScansLabel);

  return (
    <Stack sx={{ gap: 3 }}>
      {checkout === "cancelled" && (
        <Alert
          variant="info"
          title="You closed the IPX checkout, nothing was charged."
        />
      )}
      <SummaryList>
        {scansLabel && <SummaryRow label="Scans">{scansLabel}</SummaryRow>}
        {volume !== undefined && (
          <SummaryRow label="Volume">
            {formatVolume(volume, distanceUnit)}
          </SummaryRow>
        )}
        {floorArea !== undefined && (
          <SummaryRow label="Gross floor area (GFA)">
            {formatFloorArea(floorArea)}
          </SummaryRow>
        )}
        <SummaryRow label="Estimated points">
          {ESTIMATED_POINT_COUNT_LABEL}
        </SummaryRow>
        {bytes !== undefined && (
          <SummaryRow label="Estimated size">
            {`~${humanReadableBytes(bytes, 1)}`}
          </SummaryRow>
        )}
      </SummaryList>
      <FaroText variant="bodyS" color={grey[600]}>
        IPX is a third-party service: it handles the order, its terms, the
        payment and the model. FARO support helps with Sphere XG.
      </FaroText>
    </Stack>
  );
}
